const React = require('react');
const PropTypes = require('prop-types');
const { default: Styled } = require('styled-components');
const { default: Paper } = require('@mui/material/Paper');
const { Typography } = require('@mui/material');

const UserInputs = require('components/UserInputs');
const { default: Classes } = require('./styles.scss');
const BottomButtonContainer = require('components/BottomButtonContainer');
const ScrollContainerLayout = require('layouts/ScrollContainerLayout');
const Storage = require('utils/storage');
const PersistenceConfig = require('utils/persistence-config');

const { useEffect, useRef, useState } = React;

const internals = {};

const UserInfo = module.exports = function UserInfo(props) {

    const {
        isParent,
        signupContext,
        schoolName,
        onInvalidSubmit,
        ...userInputsProps
    } = props;

    const {
        FieldsWrapper,
        HeaderText
    } = internals;

    const { joinedSchoolIds = [] } = signupContext || {};
    const alreadyBelongsToSchools = joinedSchoolIds.length > 0;
    const titlePrefix = 'Complete';
    const profileLabel = schoolName
        ? `${schoolName} profile`
        : 'profile';
    const line1Text = `${titlePrefix} your ${profileLabel}`;
    const headerLine1Ref = useRef(null);
    const [line1FontSize, setLine1FontSize] = useState(null);

    useEffect(() => {

        document.body.classList.add(Classes.userInfoBody);

        return () => {

            document.body.classList.remove(Classes.userInfoBody);
        };
    }, []);

    // Keep line 1 on a single line by scaling its font size to the available width.
    // Longer school names and narrower devices produce a smaller font, so the whole
    // title (e.g. "Complete your Central Connecticut State University profile") always
    // fits on one row without ever running off the screen.
    useEffect(() => {

        if (typeof window === 'undefined') {
            return undefined;
        }

        const fitLine1 = () => {

            const line1Element = headerLine1Ref.current;
            const container = line1Element && line1Element.parentElement
                ? line1Element.parentElement.parentElement
                : null;

            if (!line1Element || !container) {
                return;
            }

            const containerStyle = window.getComputedStyle(container);
            const horizontalPadding = (parseFloat(containerStyle.paddingLeft) || 0)
                + (parseFloat(containerStyle.paddingRight) || 0);
            const widthBuffer = 4;
            const availableWidth = container.clientWidth - horizontalPadding - widthBuffer;

            if (availableWidth <= 0) {
                return;
            }

            const maxFontSize = window.innerWidth > 600 ? 20 : 18;
            const minFontSize = 9;
            const baseFontSize = 16;

            // Measure the text width at a known font size. Because the text is on a
            // single line its width scales roughly linearly with the font size, so we
            // can compute the fitting size directly instead of iterating.
            const previousFontSize = line1Element.style.fontSize;
            line1Element.style.fontSize = `${baseFontSize}px`;
            const measuredWidth = line1Element.scrollWidth;
            line1Element.style.fontSize = previousFontSize;

            if (measuredWidth <= 0) {
                return;
            }

            const fittedFontSize = baseFontSize * (availableWidth / measuredWidth);
            const clampedFontSize = Math.min(maxFontSize, Math.max(minFontSize, fittedFontSize));

            setLine1FontSize(Number(clampedFontSize.toFixed(2)));
        };

        fitLine1();

        const line1Element = headerLine1Ref.current;
        const observedTarget = line1Element && line1Element.parentElement
            ? line1Element.parentElement.parentElement
            : null;
        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(fitLine1)
            : null;

        if (resizeObserver && observedTarget) {
            resizeObserver.observe(observedTarget);
        }

        window.addEventListener('resize', fitLine1);

        return () => {

            if (resizeObserver) {
                resizeObserver.disconnect();
            }

            window.removeEventListener('resize', fitLine1);
        };
    }, [line1Text]);

    return (
        <UserInputs
            {...userInputsProps}
            mode='signup'
            onInvalidSubmit={onInvalidSubmit}
        >
            {({
                renderFields,
                uiState,
                submit
            }) => {
                const headerFontSizeStyle = line1FontSize
                    ? { fontSize: `${line1FontSize}px` }
                    : undefined;

                return (
                    <ScrollContainerLayout>
                        <BottomButtonContainer
                            btnLabel={alreadyBelongsToSchools ? 'Confirm' : 'Save'}
                            btnClassName={Classes.signupBottomBtn}
                            onBtnClick={internals.wrapSubmitForDebug(submit)}
                            disabled={uiState.submitting || uiState.uploadingProfilePic}
                        >
                            <div className={`${Classes.fixedCaps} ${Classes.header}`}>
                                <Paper className={Classes.paper} elevation={1}>
                                    <div className='contentWrapper'>
                                        <HeaderText tabIndex={0}>
                                            <span
                                                ref={headerLine1Ref}
                                                className={Classes.headerLine1}
                                                style={headerFontSizeStyle}
                                            >
                                                {line1Text}
                                            </span>
                                            <span
                                                className={Classes.headerLine2}
                                                style={headerFontSizeStyle}
                                            >
                                                {isParent ? 'to start meeting other families' : 'then meet your peers!'}
                                            </span>
                                        </HeaderText>
                                    </div>
                                </Paper>
                            </div>
                            <div className={`${Classes.main}`}>
                                {renderFields({ fieldsWrapper: FieldsWrapper })}
                            </div>
                        </BottomButtonContainer>
                    </ScrollContainerLayout>
                );
            }}
        </UserInputs>
    );
};

UserInfo.propTypes = {
    ...UserInputs.propTypes,
    isParent: PropTypes.bool,
    signupContext: PropTypes.shape({
        joinedSchoolIds: PropTypes.array
    }).isRequired,
    onGoBack: PropTypes.func.isRequired,
    onLogout: PropTypes.func,
    isAuthenticated: PropTypes.bool.isRequired,
    schoolName: PropTypes.string
};

internals.FieldsWrapper = Styled.div`
    margin: 0 auto;
    @media screen and (min-width: 360px) {
        max-width: 400px;
    }
    @media screen and (min-width: 600px) {
        max-width: 600px;
    }

    button:hover, div[role="button"]:hover {
        opacity: 0.9;
        & > div > div {
            background-color: initial !important;
            opacity: 1;
        }
    }
`;

internals.formDebugEnabled = () => typeof window !== 'undefined'
    && (
        Storage.getItem(PersistenceConfig.keys.debug.formEnabled) === '1'
        || new URLSearchParams(window.location.search).get('formDebug') === '1'
    );

internals.wrapSubmitForDebug = (submit) => (...args) => {

    if (typeof window !== 'undefined') {
        window.__FORM_DEBUG_LAST_SAVE_CLICK__ = {
            at: new Date().toISOString(),
            argsCount: args.length,
            button: 'signup-user-info-save'
        };
    }

    if (internals.formDebugEnabled()) {
        // eslint-disable-next-line no-console
        console.log('[FormDebug] signup Save clicked', window.__FORM_DEBUG_LAST_SAVE_CLICK__);
    }

    return submit(...args);
};

internals.HeaderText = Styled(Typography).attrs({
    variant: 'h1'
})`
    font-size: 20px;
    white-space: normal;
    @media screen and (max-width: 600px) {
        font-size: 18px;
    }
`;
